import React from "react";
import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  Select,
  SimpleGrid,
  useColorMode,
} from "@chakra-ui/react";
import { BsFilter } from "react-icons/bs";
import TradeFilterChips from "./TradeFilterChips";

/**
 * The filter bar shown above the trade table:
 *   [Filters toggle] [active-filter chips]
 *   ┌──────────── form (trade type / security type / direction /
 *                       ticker autocomplete / from / to / tag) ────┐
 *   [Apply]  [Clear]
 *
 * State remains owned by the parent (Summary) — this component is purely
 * presentational so the existing handlers (handleSubmitFilter, etc.) keep
 * their access to `dispatch`, `getTradesPage`, and the filter state vars.
 *
 * Migrating to useFiltersWithPersist (TRADE-912) is a follow-up: it requires
 * collapsing the seven filter_* state vars into a single object, which
 * touches the page-change handlers and the localStorage hydration effect.
 */
const TradeFilters = ({
  // active-filter chips
  filters,
  tags,
  onRemoveFilter,
  labelMap,
  keysToSkip,

  // collapse toggle
  toggleFilter,
  onToggleFilter,

  // filter values + setters
  filter_trade_type,
  setFilterTradeType,
  filter_security_type,
  setFilterSecurityType,
  filter_trade_direction,
  setFilterTradeDirection,
  filter_from_date,
  setFilterFromDate,
  filter_to_date,
  setFilterToDate,
  filter_tag,
  setFilterTag,

  // ticker autocomplete
  searchTickerValue,
  selectedTickerValue,
  onTickerInputChange,
  onTickerInputClick,
  isDropdownOpen,
  isLoading,
  searchTickerResultItems,

  // misc
  maxDate,
  onSubmit,
  onClear,
}) => {
  const { colorMode } = useColorMode();

  return (
    <Box position="relative" zIndex={2}>
      <HStack spacing={3} flexWrap="wrap">
        <Button
          size="sm"
          variant="ghost"
          colorScheme="gray"
          leftIcon={<Icon as={BsFilter} />}
          onClick={onToggleFilter}
          borderRadius="md"
        >
          Filters
        </Button>
        <TradeFilterChips
          filters={filters}
          tags={tags}
          onRemoveFilter={onRemoveFilter}
          labelMap={labelMap}
          keysToSkip={keysToSkip}
        />
      </HStack>

      <Collapse
        in={toggleFilter}
        animateOpacity
        style={{ overflow: toggleFilter ? "visible" : "hidden" }}
      >
        <Box
          mt={3}
          p={4}
          bg={colorMode === "light" ? "white" : "gray.700"}
          rounded="lg"
          boxShadow="sm"
        >
          <SimpleGrid columns={{ base: 2, md: 4, lg: 7 }} spacing={3}>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Trade Type</FormLabel>
              <Select
                size="sm"
                placeholder="All"
                value={filter_trade_type}
                onChange={(e) => setFilterTradeType(e.target.value)}
              >
                <option>Swing Trade</option>
                <option>Day Trade</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Security Type</FormLabel>
              <Select
                size="sm"
                placeholder="All"
                value={filter_security_type}
                onChange={(e) => setFilterSecurityType(e.target.value)}
              >
                <option>Options</option>
                <option>Shares</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Direction</FormLabel>
              <Select
                size="sm"
                placeholder="All"
                value={filter_trade_direction}
                onChange={(e) => setFilterTradeDirection(e.target.value)}
              >
                <option>Long</option>
                <option>Short</option>
                <option>Call</option>
                <option>Put</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Ticker</FormLabel>
              <div className="ticker-search">
                <Input
                  size="sm"
                  type="name"
                  placeholder="Enter Ticker"
                  value={selectedTickerValue ? selectedTickerValue : searchTickerValue}
                  onChange={onTickerInputChange}
                  onClick={onTickerInputClick}
                />
                {isDropdownOpen && (
                  <ul
                    className={
                      colorMode === "light" ? "search-dropdown" : "search-dropdown search-dropdown-dark"
                    }
                  >
                    {isLoading ? <div>Loading...</div> : searchTickerResultItems}
                  </ul>
                )}
              </div>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>From Date</FormLabel>
              <Input
                size="sm"
                value={filter_from_date}
                type="date"
                max={maxDate}
                min="1900-01-01"
                onChange={(e) => setFilterFromDate(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>To Date</FormLabel>
              <Input
                size="sm"
                value={filter_to_date}
                type="date"
                max={maxDate}
                min={filter_from_date !== "" ? filter_from_date : "1900-01-01"}
                onChange={(e) => setFilterToDate(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs" mb={1}>Tag</FormLabel>
              <Select
                size="sm"
                value={filter_tag}
                placeholder="All Tags"
                onChange={(e) => setFilterTag(e.target.value)}
              >
                {tags.map((tag) => (
                  <option key={tag.tag_id} value={tag.tag_id}>
                    {tag.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          </SimpleGrid>
          <HStack mt={3} spacing={2}>
            <Button size="sm" colorScheme="blue" onClick={onSubmit}>
              Apply
            </Button>
            <Button
              size="sm"
              variant="outline"
              colorScheme="gray"
              borderColor={colorMode === "light" ? "gray.300" : undefined}
              onClick={onClear}
            >
              Clear
            </Button>
          </HStack>
        </Box>
      </Collapse>
    </Box>
  );
};

export default TradeFilters;
